import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { SvText } from "@/components";
import { SvSeatsChart } from "@/components/SvSeatsChart/SvSeatsChart.tsx";
/*import {
  SvSegmentedButtons,
  SvSegmentedButtonsOptionType,
} from "@/components/SvSegmentedButtons/SvSegmentedButtons.tsx";*/
import { SvVoteChart } from "@/components/SvVoteChart/SvVoteChart.tsx";
import {
  usePvProjQuery,
  useSeatsProjQuery,
} from "@/data/queries/RidingQueries";
import { cn } from "@/utils/tailwind.ts";

type ProjectionsBlockProps = {
  election: string;
  className?: string;
};

/*const seatTabs: SvSegmentedButtonsOptionType[] = [
  {
    key: "without_split",
    i18nKey: "projections.without_split",
  },
  {
    key: "with_split",
    i18nKey: "projections.with_split",
  },
];*/

export const ProjectionsBlock = ({
  election,
  className,
}: ProjectionsBlockProps) => {
  const { data: seatsProjections } = useSeatsProjQuery(election);
  const { data: popularVoteProjections } = usePvProjQuery(election);
  const { t } = useTranslation();

  const [seatTab, /*setSeatTab*/] = useState<"without_split" | "with_split">(
    "with_split",
  );

  const seatCount = useMemo(() => {
    if (!seatsProjections) return 0;
    return seatsProjections?.seats?.reduce((sum, party) => {
      return sum + party.seats;
    }, 0);
  }, [seatsProjections]);

  return (
    <div className={cn("py-16", className)}>
      <div className="container text-center">
        <SvText
          variant="subtitle"
          className="mb-6 text-2xl font-extrabold"
          i18nKey="ridings.projections"
          i18nValues={{ type: t("common.federal"), area: "Canada" }}
        />
        <div className="flex flex-col items-stretch justify-center gap-4 lg:flex-row">
          <div className="border-ind-grey-50 w-full grow-0 basis-full rounded-lg border bg-white px-8 py-6 md:w-auto lg:grow">
            <SvText
              i18nKey="projections.seat_projection"
              i18nValues={{
                seatCount: seatCount?.toString() ?? "343",
              }}
              className="mb-2 text-xl font-extrabold"
            />
            <div className="flex flex-col items-center justify-center 2xl:flex-row 2xl:gap-4">
              <SvText i18nKey="projections.moe" className="font-bold">
                : <span className="font-normal">+/- 10 %</span>
              </SvText>
              <SvText i18nKey="projections.confidence" className="font-bold">
                : <span className="font-normal">95%</span>
              </SvText>
              <SvText i18nKey="projections.updated" className="font-bold">
                :{" "}
                <span className="font-normal">
                  {seatsProjections?.date.toLocaleString(DateTime.DATE_MED)}
                </span>
              </SvText>
            </div>
            <div className="mt-6 mb-3 flex flex-col justify-center gap-4 pb-3">
              {/*<SvSegmentedButtons
                options={seatTabs}
                selected={seatTab}
                onOptionSelect={key =>
                  setSeatTab(key as "with_split" | "without_split")
                }
              />*/}
              <SvSeatsChart
                data={
                  (seatTab === "with_split"
                    ? seatsProjections?.seats
                    : seatsProjections?.seatsUnsplit) ?? []
                }
              />
            </div>
          </div>
          <div className="border-ind-grey-50 flex w-full grow basis-full flex-col justify-start rounded-lg border bg-white px-8 py-6 md:w-auto">
            <SvText
              i18nKey="projections.popular_vote"
              className="mb-2 text-xl font-extrabold"
            />
            <div className="flex flex-col items-center justify-center 2xl:flex-row 2xl:gap-4">
              <SvText i18nKey="projections.moe" className="font-bold">
                : <span className="font-normal">3.5%</span>
              </SvText>
              <SvText i18nKey="projections.confidence" className="font-bold">
                : <span className="font-normal">95%</span>
              </SvText>
              <SvText i18nKey="projections.updated" className="font-bold">
                :{" "}
                <span className="font-normal">
                  {popularVoteProjections?.date.toLocaleString(
                    DateTime.DATE_MED,
                  )}
                </span>
              </SvText>
            </div>
            <SvVoteChart
              className="bg-ind-grey-40 my-auto rounded-md p-4"
              data={popularVoteProjections?.projection ?? []}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
